import React, { useState } from "react";
import { Box, Button, Checkbox, Divider, ListItemIcon, ListItemText, Menu, MenuItem, Stack, TextField, Tooltip, Typography } from "@mui/material";
import { ChevronDown, Zap } from "lucide-react";
import { PersonAvatar, T, TYPE, ghostButtonSx } from "../../user/complaints/shared";
import { TypeIcon } from "../ui";

// Board toolbar filters: project-member avatars (assignee), epic and issue type.
// Values are ids ("none" = unassigned / no epic); the board turns them into
// server query conditions, so paging per column stays correct.

const toggle = (list, v) => (list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);

const Ring = ({ active, children, title, onClick, z }) => (
  <Tooltip title={title}>
    <Box component="button" type="button" onClick={onClick} aria-pressed={active}
      sx={{
        p: 0, ml: "-6px", border: `2px solid ${active ? T.brand : T.surface}`, borderRadius: "50%", bgcolor: T.surface, cursor: "pointer", display: "inline-flex",
        position: "relative", zIndex: active ? 20 : z, boxShadow: active ? `0 0 0 2px ${T.brandSoft}` : "none", transition: "transform .12s",
        "&:hover": { transform: "translateY(-2px)", zIndex: 30 }, "&:first-of-type": { ml: 0 },
      }}>
      {children}
    </Box>
  </Tooltip>
);

/** Avatars of this project's members only; click to show their issues (several can be picked). */
export const PeopleFilter = ({ people, value, onChange, max = 6 }) => {
  const [anchor, setAnchor] = useState(null);
  const [q, setQ] = useState("");
  const shown = people.slice(0, max);
  const rest = people.slice(max);
  // the menu lists everyone (searchable), so any name can be picked from one place
  const found = people.filter((p) => !q.trim() || p.name.toLowerCase().includes(q.trim().toLowerCase()));
  const restActive = rest.filter((p) => value.includes(String(p.id))).length;
  return (
    <Stack direction="row" alignItems="center">
      {shown.map((p, i) => (
        <Ring key={String(p.id)} title={p.name} active={value.includes(String(p.id))} z={max - i} onClick={() => onChange(toggle(value, String(p.id)))}>
          <PersonAvatar name={p.name} size={30} />
        </Ring>
      ))}
      {rest.length > 0 && (
        <Ring title={`${rest.length} more`} active={restActive > 0} z={0} onClick={(e) => setAnchor(e.currentTarget)}>
          <Box sx={{ width: 30, height: 30, borderRadius: "50%", bgcolor: T.track, color: T.inkSecondary, fontSize: TYPE.micro, fontWeight: 700, display: "grid", placeItems: "center" }}>
            +{rest.length}
          </Box>
        </Ring>
      )}
      <Ring title="Unassigned" active={value.includes("none")} z={0} onClick={() => onChange(toggle(value, "none"))}>
        <Box sx={{ width: 30, height: 30, borderRadius: "50%", border: `1.5px dashed ${T.borderStrong}`, bgcolor: T.surfaceMuted, boxSizing: "border-box" }} />
      </Ring>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => { setAnchor(null); setQ(""); }} PaperProps={{ sx: { width: 260, maxHeight: 420 } }} autoFocus={false}>
        <Box sx={{ px: 1.5, pb: 1 }} onKeyDown={(e) => e.stopPropagation()}>
          <TextField size="small" fullWidth autoFocus placeholder="Search people…" value={q} onChange={(e) => setQ(e.target.value)} />
        </Box>
        {!found.length && <Typography sx={{ px: 2, py: 1, fontSize: TYPE.small, color: T.inkMuted }}>No one found</Typography>}
        {found.map((p) => (
          <MenuItem key={String(p.id)} dense onClick={() => onChange(toggle(value, String(p.id)))}>
            <Checkbox size="small" checked={value.includes(String(p.id))} sx={{ p: 0.25, mr: 1 }} />
            <ListItemIcon sx={{ minWidth: 32 }}><PersonAvatar name={p.name} size={22} /></ListItemIcon>
            <ListItemText primaryTypographyProps={{ fontSize: TYPE.small }}>{p.name}</ListItemText>
          </MenuItem>
        ))}
      </Menu>
    </Stack>
  );
};

/** Dropdown with checkboxes (Epic, Type). options: [{ value, label, icon }] */
export const MultiFilter = ({ label, options, value, onChange, emptyText = "Nothing to filter by" }) => {
  const [anchor, setAnchor] = useState(null);
  const active = value.length > 0;
  return (
    <>
      <Button size="small" variant="outlined" endIcon={<ChevronDown size={14} />} onClick={(e) => setAnchor(e.currentTarget)}
        sx={{ ...ghostButtonSx, ...(active ? { borderColor: T.brand, color: T.brand, bgcolor: T.brandSoft } : {}) }}>
        {label}{active ? ` (${value.length})` : ""}
      </Button>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)} PaperProps={{ sx: { minWidth: 220, maxHeight: 380 } }}>
        {!options.length && <Typography sx={{ px: 2, py: 1, fontSize: TYPE.small, color: T.inkMuted }}>{emptyText}</Typography>}
        {options.map((o) => (
          <MenuItem key={o.value} dense onClick={() => onChange(toggle(value, o.value))}>
            <Checkbox size="small" checked={value.includes(o.value)} sx={{ p: 0.25, mr: 1 }} />
            {o.icon && <ListItemIcon sx={{ minWidth: 26 }}>{o.icon}</ListItemIcon>}
            <ListItemText primaryTypographyProps={{ fontSize: TYPE.small, noWrap: true, title: o.label }}>{o.label}</ListItemText>
          </MenuItem>
        ))}
        {active && [
          <Divider key="d" />,
          <MenuItem key="c" dense onClick={() => onChange([])}><ListItemText primaryTypographyProps={{ fontSize: TYPE.small, color: T.brand }}>Clear {label.toLowerCase()}</ListItemText></MenuItem>,
        ]}
      </Menu>
    </>
  );
};

export const typeOptions = (ctx, project) =>
  ctx.issueTypes
    .filter((t) => t.isActive !== false && (!project.issueTypeIds?.length || project.issueTypeIds.map(String).includes(String(t._id))))
    .map((t) => ({ value: String(t._id), label: t.name, icon: <TypeIcon typeId={t._id} size={15} /> }));

export const epicOptions = (epics) => [
  ...epics.map((e) => ({ value: String(e._id), label: `${e.key ? `${e.key} ` : ""}${e.summary}`, icon: <Zap size={15} color="#7A5AF8" /> })),
  { value: "none", label: "Issues without epic" },
];

/** Selected filters -> server query conditions. */
export const filterConditions = ({ people, types, epics }) => [
  ...(people.length ? [{ field: "assignee", op: "in", value: people }] : []),
  ...(types.length ? [{ field: "type", op: "in", value: types }] : []),
  ...(epics.length ? [{ field: "epic", op: "in", value: epics }] : []),
];

/** Dropdown menu for board Quick Filters matching Jira interface. */
export const QuickFiltersDropdown = ({ filters = [], activeIds = [], onChange, onClear }) => {
  const [anchor, setAnchor] = useState(null);
  const activeCount = activeIds.length;

  return (
    <>
      <Button
        size="small"
        variant="outlined"
        endIcon={<ChevronDown size={14} />}
        onClick={(e) => setAnchor(e.currentTarget)}
        sx={{
          ...ghostButtonSx,
          height: 36,
          textTransform: "none",
          fontWeight: 600,
          fontSize: TYPE.small,
          whiteSpace: "nowrap",
          ...(activeCount ? { borderColor: T.brand, color: T.brand, bgcolor: T.brandSoft } : {}),
        }}
      >
        Quick filters{activeCount ? ` (${activeCount})` : ""}
      </Button>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        PaperProps={{ sx: { minWidth: 200, maxHeight: 380, borderRadius: "8px" } }}
      >
        {filters.map((q) => {
          const checked = activeIds.includes(q._id);
          return (
            <MenuItem
              key={q._id}
              dense
              onClick={() => {
                onChange(checked ? activeIds.filter((id) => id !== q._id) : [...activeIds, q._id]);
              }}
            >
              <Checkbox size="small" checked={checked} sx={{ p: 0.25, mr: 1 }} />
              <ListItemText primaryTypographyProps={{ fontSize: TYPE.small }}>{q.name}</ListItemText>
            </MenuItem>
          );
        })}
        {activeCount > 0 && [
          <Divider key="d" />,
          <MenuItem
            key="clear"
            dense
            onClick={() => {
              onClear();
              setAnchor(null);
            }}
          >
            <ListItemText primaryTypographyProps={{ fontSize: TYPE.small, color: T.brand }}>
              Clear quick filters
            </ListItemText>
          </MenuItem>,
        ]}
      </Menu>
    </>
  );
};

